import {
  Button,
  color,
  ErrorBanner,
  IconSearch,
  LoaderSpinner,
  SpaceBetween,
  spacing,
  styled,
  TextElement,
  TextField,
  useTranslation,
} from '@lessonup/ui-components';
import { isString } from 'lodash';
import React, { useEffect, useRef } from 'react';
import { MediaHubImage, MediaHubProvider } from '../../MediaHubImageSearch.graphql';
import type { MediaHubImageSearchFeatureProps } from '../../MediaHubImageSearchFeature';
import { TRANSLATION_NAMESPACE } from '../../consts';

export interface MediaHubImageSearchContentProps
  extends Omit<MediaHubImageSearchFeatureProps, 'initialQuery' | 'onSelection' | 'trackingContext'> {
  results: MediaHubImage[];
  provider?: MediaHubProvider;
  onSearch: (query: string) => void;
  onMediaSelection: (imageUrl: URL, trackDownloadUrl?: URL) => void;
  loading: boolean;
  hasError: boolean;
}

export const MediaHubImageSearchContent = ({
  results,
  provider,
  onSearch,
  onMediaSelection,
  loading,
  hasError,
}: MediaHubImageSearchContentProps) => {
  const { t } = useTranslation(TRANSLATION_NAMESPACE);
  const resultsSectionRef = useRef<HTMLDivElement>(null);

  // Scroll to top when new results arrive
  useEffect(() => {
    if (results.length > 0 && resultsSectionRef.current) {
      resultsSectionRef.current.scrollTop = 0;
    }
  }, [results]);

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const query = data.get('query');
    if (!isString(query)) {
      return;
    }

    const trimmed = query.trim();
    if (trimmed.length < 1) {
      return;
    }

    onSearch(trimmed);
  };

  return (
    <ContentWrapper direction="y" spacing={spacing.size8}>
      <form onSubmit={onSubmit}>
        <FormFieldWrapper spacing={spacing.size8} direction="x" alignItems="center">
          <TextField
            placeholder={t('searchInput.placeholder')}
            type="text"
            name="query"
            aria-label={t('searchInput.label')}
            endSlot={
              <Button
                size="small"
                buttonType="secondary"
                showStroke
                type="submit"
                buttonShape="rect"
                loading={loading}
                iconStart={<IconSearch />}
              >
                {t('searchInput.buttonText')}
              </Button>
            }
          />
        </FormFieldWrapper>
      </form>

      <ErrorBanner />

      <ResultsSection ref={resultsSectionRef}>
        {!hasError &&
          (loading ? (
            <LoadingWrapper>
              <LoaderSpinner />
            </LoadingWrapper>
          ) : (
            <>
              {!results.length ? (
                <LoadingWrapper>
                  <TextElement textStyle="caption">{t('noResults')}</TextElement>
                </LoadingWrapper>
              ) : (
                <ResultsList>
                  {results.map((result) => (
                    <li key={result.id}>
                      <ResultItem direction="y" spacing={spacing.size2} as="figure">
                        <button
                          onClick={() => {
                            onMediaSelection(
                              new URL(result.urls.large),
                              result.urls.trackDownloadUrl ? new URL(result.urls.trackDownloadUrl) : undefined
                            );
                          }}
                          aria-label={t('resultItemLabel')}
                          type="button"
                        >
                          <ImageContainer width={result.width} height={result.height}>
                            <ResultImg
                              src={result.urls.thumbnail.toString()}
                              alt={result.alt ?? t('resultItemAlt', { author: result.author?.displayName ?? '' })}
                              loading="lazy"
                            />
                          </ImageContainer>
                        </button>

                        {result.author && (
                          <figcaption>
                            <Author textStyle="caption">
                              {result.author.url ? (
                                <a href={result.author.url.toString()} target="_blank" rel="noopener noreferrer">
                                  {result.author.displayName}
                                </a>
                              ) : (
                                result.author.displayName
                              )}
                            </Author>
                          </figcaption>
                        )}
                      </ResultItem>
                    </li>
                  ))}
                </ResultsList>
              )}
            </>
          ))}
      </ResultsSection>

      {provider ? (
        <TextElement textStyle="label">
          <a href={provider.url} target="_blank" rel="noopener noreferrer">
            {t('providerReference', { name: provider.name })}
          </a>
        </TextElement>
      ) : null}
    </ContentWrapper>
  );
};

const ContentWrapper = styled(SpaceBetween)`
  height: 100%;
`;

const ResultsSection = styled.div`
  overflow-y: auto;
  flex: 1;
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

const ResultsList = styled.ul`
  column-count: 2;
  column-gap: ${spacing.size8};
  margin: 0; // reset
  padding: 0; // reset

  li {
    padding-left: 0; // reset
    list-style: none; // reset
  }
`;

const FormFieldWrapper = styled(SpaceBetween)`
  & > div:first-of-type {
    flex-grow: 1;
    min-width: 100px;
  }
`;

const ResultItem = styled(SpaceBetween)`
  width: 100%;
  padding-bottom: ${spacing.size8};
  break-inside: avoid;
`;

const ImageContainer = styled.div<{ width: number; height: number }>`
  background: ${color.additional.disabled.background};
  position: relative;
  width: 100%;
  padding-bottom: ${(props) => (props.height / props.width) * 100}%;
`;

const ResultImg = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Author = styled(TextElement)`
  color: ${color.neutralNew.white.onSubjacent};
`;
